import { useEffect, useState } from "react";
import { toast } from "sonner";
import { History, RotateCcw, Columns2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { KbCompareDialog } from "@/components/KbCompareDialog";

const when = (iso) => (iso ? new Date(iso).toLocaleString([], {
  month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

/** Revision history for one article, newest first, with one-click restore. */
export function KbRevisionsPanel({ slug, onRestored }) {
  const [rows, setRows] = useState([]);
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState("");
  const [compare, setCompare] = useState(null);

  const load = () => api.get(`/kb/articles/${slug}/revisions`)
    .then((r) => { setRows(r.data.revisions || []); setCurrent(r.data.current || null); })
    .catch(() => {});

  useEffect(() => { if (slug) load(); }, [slug]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Look before you leap: show the saved version next to the live one.
  const openCompare = async (rev) => {
    setBusy(rev.rev_id);
    try {
      const { data } = await api.get(`/kb/articles/${slug}/compare/${rev.rev_id}`);
      setCompare(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const restore = async (rev) => {
    if (!window.confirm(`Restore the version saved ${when(rev.at)} by ${rev.by_name}? The current version is snapshotted first.`)) return;
    setBusy(rev.rev_id);
    try {
      const { data } = await api.post(`/kb/articles/${slug}/revisions/${rev.rev_id}/restore`);
      toast.success("Revision restored");
      onRestored?.(data);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  return (
    <div className="space-y-2" data-testid="kb-revisions-panel">
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
        <History className="h-3.5 w-3.5" /> Revision history
      </div>
      {current && (
        <p className="text-xs text-slate-500 dark:text-slate-400" data-testid="kb-revisions-current">
          Current: edited by <span className="font-medium text-slate-700 dark:text-slate-200">{current.edited_by || "—"}</span> {when(current.edited_at)}
        </p>
      )}
      {rows.length === 0 ? (
        <p className="text-xs text-slate-400 dark:text-slate-500" data-testid="kb-revisions-empty">No earlier versions yet — the next save creates one.</p>
      ) : (
        <ul className="max-h-64 space-y-1 overflow-y-auto">
          {rows.map((r) => (
            <li key={r.rev_id} data-testid={`kb-revision-${r.rev_id}`}
              className="rounded-md border border-slate-100 dark:border-slate-800 px-2.5 py-2 text-xs">
              <div className="flex items-center justify-between gap-2">
                <span className="min-w-0">
                  <span className="font-medium text-slate-700 dark:text-slate-200">{r.by_name || "—"}</span>
                  <span className="text-slate-400 dark:text-slate-500"> · {when(r.at)}</span>
                </span>
                <span className="flex shrink-0 items-center gap-1">
                  <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[11px]"
                    data-testid={`kb-revision-compare-${r.rev_id}`}
                    disabled={busy === r.rev_id} onClick={() => openCompare(r)}>
                    <Columns2 className="mr-1 h-3 w-3" /> Compare
                  </Button>
                  <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[11px]"
                    data-testid={`kb-revision-restore-${r.rev_id}`}
                    disabled={busy === r.rev_id} onClick={() => restore(r)}>
                    <RotateCcw className="mr-1 h-3 w-3" /> Restore
                  </Button>
                </span>
              </div>
              <div className="mt-0.5 text-slate-400 dark:text-slate-500">
                {r.word_count || 0} words{r.note ? ` · ${r.note}` : ""}
              </div>
            </li>
          ))}
        </ul>
      )}
      {compare && <KbCompareDialog c={compare} onClose={() => setCompare(null)} />}
    </div>
  );
}
